import { Controller } from '@hotwired/stimulus';
import {
  batchSetNodeKVPair,
  feedbackCardRatingsIncomplete,
  mergePassiveInactiveKvIntoState,
  normalizeActions,
  patchDocumentUserState,
  requiresAuthoritativeApplyState,
} from '../../utils/case_experience/case_experience_in_rails';

function feedbackCardScope(element) {
  return element?.closest?.('.feedback-card-content') || element?.closest?.('.doc-section.feedback_card');
}

function refreshFeedbackSubmitButtons(application, scopeEl) {
  if (!scopeEl) return;
  scopeEl.querySelectorAll('[data-controller~="case-experience--button-action"]').forEach((el) => {
    const c = application.getControllerForElementAndIdentifier(el, 'case-experience--button-action');
    c?.updateButtonState?.();
  });
}

/** Feedback card: textarea editable once every required rating bar has a value. */
function syncFeedbackCardTextarea(scope) {
  if (!scope) return;
  const textarea = scope.querySelector('textarea');
  if (!textarea) return;

  textarea.disabled = feedbackCardRatingsIncomplete(scope);
}

export default class extends Controller {
  static targets = ['star'];
  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    maxValue: Number,
    currentValue: Number,
    inactive: Number,
    onClick: Array,
  };

  connect() {
    this.focusedIndex = null;
    this._inactiveContainerKeydown = null;
    this.disableRating();
    this.updateStarDisplay();
    requestAnimationFrame(() => this.refreshSubmitButtonInFeedbackScope());
  }

  currentValueValueChanged() {
    this.updateStarDisplay();
    const scope = feedbackCardScope(this.element);
    if (scope) syncFeedbackCardTextarea(scope);
  }

  inactiveValueChanged() {
    this.disableRating();
    this.updateStarDisplay();
  }

  disconnect() {
    if (this._inactiveContainerKeydown) {
      this.element.removeEventListener('keydown', this._inactiveContainerKeydown);
      this._inactiveContainerKeydown = null;
    }
  }

  focusStarByRatingIndex(ratingIndex) {
    this.element.querySelector(`[data-rating-index="${ratingIndex}"]`)?.focus();
  }

  refreshSubmitButtonInFeedbackScope() {
    const scope = feedbackCardScope(this.element);
    if (!scope) return;
    refreshFeedbackSubmitButtons(this.application, scope);
  }

  disableRating() {
    const isInactive = this.inactiveValue === 2;
    this.element.classList.toggle('inactive', isInactive);

    if (this._inactiveContainerKeydown) {
      this.element.removeEventListener('keydown', this._inactiveContainerKeydown);
      this._inactiveContainerKeydown = null;
    }

    if (isInactive) {
      // One tab stop for the whole locked bar so focus traps and Tab order can reach this
      // section (stars use tabindex -1 until the bar is unlocked).
      this.element.tabIndex = 0;
      this.element.setAttribute('aria-disabled', 'true');
      this._inactiveContainerKeydown = (e) => {
        if (e.key === 'Tab') return;
        if (
          e.key === 'Enter' ||
          e.key === ' ' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown'
        ) {
          e.preventDefault();
        }
      };
      this.element.addEventListener('keydown', this._inactiveContainerKeydown);
      this.starTargets.forEach((star) => {
        star.disabled = true;
        star.tabIndex = -1;
        star.setAttribute('aria-disabled', 'true');
      });
    } else {
      const hadContainerFocus = document.activeElement === this.element;
      this.element.removeAttribute('tabindex');
      this.element.removeAttribute('aria-disabled');
      const activeTabValue = this.currentValueValue > 0 ? this.currentValueValue : 1;
      this.starTargets.forEach((star) => {
        const ratingIndex = Number(star.dataset.ratingIndex);
        const value = ratingIndex + 1;
        star.disabled = false;
        star.removeAttribute('aria-disabled');
        star.tabIndex = value === activeTabValue ? 0 : -1;
      });
      if (hadContainerFocus) {
        requestAnimationFrame(() => this.focusStarByRatingIndex(0));
      }
    }

    const scope = feedbackCardScope(this.element);
    if (scope) {
      syncFeedbackCardTextarea(scope);
      this.refreshSubmitButtonInFeedbackScope();
    }
  }

  selectRating(event) {
    if (this.inactiveValue === 2) return;
    const { ratingIndex } = event.currentTarget.dataset || {};
    if (ratingIndex === undefined) return;
    const index = Number(ratingIndex);
    if (Number.isNaN(index)) return;
    void this.updateRating(index + 1);
  }

  handleKeyDown(event) {
    if (this.inactiveValue === 2) return;
    const currentStar = event.currentTarget;
    const { ratingIndex } = currentStar.dataset || {};
    if (ratingIndex === undefined) return;
    const index = Number(ratingIndex);
    if (Number.isNaN(index)) return;
    // Enter/Space: native <button> click owns activation (avoids double PATCH).
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown': {
        event.preventDefault();
        const targetIndex = index > 0 ? index - 1 : this.maxValueValue - 1;
        void this.updateRating(targetIndex + 1);
        this.focusStarByRatingIndex(targetIndex);
        break;
      }
      case 'ArrowRight':
      case 'ArrowUp': {
        event.preventDefault();
        const targetIndex = index < this.maxValueValue - 1 ? index + 1 : 0;
        void this.updateRating(targetIndex + 1);
        this.focusStarByRatingIndex(targetIndex);
        break;
      }
    }
  }

  async updateRating(newValue) {
    this.currentValueValue = newValue;
    this.updateStarDisplay();

    const updatedAt = Date.now();
    let state = {
      [this.nodeUidValue]: {
        value: {
          value: newValue,
          updatedAt,
        },
      },
    };
    state = mergePassiveInactiveKvIntoState(state, this.onClickValue);

    try {
      await patchDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: { document_user_state: { state } },
      });
      await this.handleOnClickActions();
      this.refreshSubmitButtonInFeedbackScope();
    } catch (error) {
      // Only log if not aborted by navigation
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Error updating rating:', error);
      }
    }
  }

  updateStarDisplay() {
    const pressedIndex =
      this.currentValueValue > 0 ? this.currentValueValue - 1 : null;
    const activeTabValue = this.currentValueValue > 0 ? this.currentValueValue : 1;

    this.starTargets.forEach((star) => {
      const ratingIndex = Number(star.dataset.ratingIndex);
      if (Number.isNaN(ratingIndex)) return;
      const value = ratingIndex + 1;
      const isActive =
        this.currentValueValue > 0 && ratingIndex < this.currentValueValue;
      const isPressed = pressedIndex !== null && ratingIndex === pressedIndex;

      star.dataset.state = isActive ? 'selected' : 'default';
      star.setAttribute('aria-checked', isPressed ? 'true' : 'false');
      if (this.inactiveValue !== 2) {
        star.tabIndex = value === activeTabValue ? 0 : -1;
      }
    });
  }

  syncOnClickActionsToDom(nodeKvPairActions) {
    nodeKvPairActions.forEach(({ arguments: { uid, key, value } }) => {
      const node = document.querySelector(`[data-uid="${uid}"]`);
      if (!node) return;
      if (key === 'inactive') {
        const ratingController = this.application.getControllerForElementAndIdentifier(
          node,
          'case-experience--rating-bar',
        );
        if (ratingController) {
          ratingController.inactiveValue = Number(value);
        }
        const buttonController = this.application.getControllerForElementAndIdentifier(
          node,
          'case-experience--button-action',
        );
        if (buttonController) {
          buttonController.inactiveValue = Number(value);
        }
      }
      if (key === 'invisible') {
        const buttonController = this.application.getControllerForElementAndIdentifier(
          node,
          'case-experience--button-action',
        );
        if (buttonController) {
          buttonController.invisibleValue = Boolean(value);
        }
      }
    });
  }

  async handleOnClickActions() {
    const normalizedActions = normalizeActions(this.onClickValue || []);
    const nodeKvPairActions = normalizedActions.filter((a) => a.action === 'setNodeKVPair');

    if (nodeKvPairActions.length > 0) {
      if (requiresAuthoritativeApplyState(normalizedActions)) {
        await batchSetNodeKVPair(
          nodeKvPairActions,
          this.documentIdValue,
          this.userIdValue,
          this.application,
        );
      }
      this.syncOnClickActionsToDom(nodeKvPairActions);
    }

    const scope = feedbackCardScope(this.element);
    if (scope) {
      syncFeedbackCardTextarea(scope);
      this.refreshSubmitButtonInFeedbackScope();
    }
  }
}
